/**
 * EliminatedPlayerScreen - Display for eliminated players.
 *
 * When a player is eliminated (life <= 0), this component:
 * - Clearly indicates the player has been eliminated
 * - Shows the final placement (FROZEN at the moment of death — derived
 *   from the authoritative order of death, never from gold/life or the
 *   current living count)
 * - Ensures no player-action controls are accessible
 * - Reacts to GameStateUpdate messages that set the player as eliminated
 * - Provides render data for UI binding
 *
 * The backend is authoritative; this component only renders elimination state
 * that the server sends via GameStateUpdate messages (phase: 'eliminated').
 */

import { AppShell, AppShellEvent } from '../AppShell';
import { PlayerData } from '../../shared/messages/MessageContracts';
import { eliminationOrderKey } from '../../shared/models/Player';

// ─── Elimination Render Data ────────────────────────────────────────────────

/**
 * Render data for the eliminated player screen.
 */
export interface EliminatedPlayerRenderData {
  /** Whether the eliminated player screen should be visible. */
  isVisible: boolean;

  /** Whether the current player has been eliminated. */
  isEliminated: boolean;

  /**
   * Final placement of the eliminated player (1-based), or null while
   * alive. FROZEN at the moment of death: 1 + the number of players who
   * survived longer (alive at death, or eliminated in a later round/phase
   * — the shared eliminationOrderKey over the authoritative
   * eliminationRound/eliminatedDuringPreparation fields). It does NOT
   * improve as the remaining players die afterwards, and gold/life never
   * factor in. Players who died in the same round/phase tie.
   */
  finalPlacement: number | null;

  /** Total number of players in the match. */
  totalPlayers: number;

  /** Number of players still alive (not including the eliminated player). */
  livingPlayersCount: number;

  /** Whether the match is over (only one player left). */
  isMatchOver: boolean;

  /** Winner player ID (if match is over). */
  winnerId: string | null;

  /** Human-readable message describing the elimination state. */
  eliminationMessage: string;

  /** Whether player-action controls should be hidden (always true when eliminated). */
  hidePlayerControls: boolean;

  /** Current round at time of elimination. */
  currentRound: number;

  /** Match ID. */
  matchId: string | null;

  /** Current player's final gold. */
  finalGold: number;

  /** Current player's final life (0 or below). */
  finalLife: number;
}

/**
 * Callback invoked when render data changes.
 */
export type EliminatedPlayerRenderCallback = (data: EliminatedPlayerRenderData) => void;

/**
 * Callback invoked when the player is eliminated.
 */
export type EliminatedCallback = (placement: number | null) => void;

// ─── Eliminated Player Screen ───────────────────────────────────────────────

export class EliminatedPlayerScreen {
  private _appShell: AppShell;
  private _playerId: string;
  private _renderCallbacks: EliminatedPlayerRenderCallback[] = [];
  private _eliminatedCallbacks: EliminatedCallback[] = [];
  private _eventUnsubscribe: (() => void) | null = null;
  private _isVisible: boolean = false;
  private _wasEliminated: boolean = false;

  constructor(appShell: AppShell) {
    this._appShell = appShell;
    this._playerId = appShell.playerId;
  }

  // ─── Public Accessors ────────────────────────────────────────────────────

  /** Returns the current render data. */
  get renderData(): EliminatedPlayerRenderData {
    return this._buildRenderData();
  }

  /** Returns true if the eliminated player screen is currently visible/active. */
  get isVisible(): boolean {
    return this._isVisible;
  }

  /** Returns true if the current player has been eliminated. */
  get isEliminated(): boolean {
    return this._buildRenderData().isEliminated;
  }

  /** Returns true if player controls should be hidden (always true when eliminated). */
  get shouldHideControls(): boolean {
    return this._buildRenderData().hidePlayerControls;
  }

  /** Returns the final placement, or null if not determined. */
  get finalPlacement(): number | null {
    return this._buildRenderData().finalPlacement;
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────

  /**
   * Show the eliminated player screen and start listening for state changes.
   */
  show(): void {
    if (this._isVisible) {
      return;
    }

    this._isVisible = true;

    this._eventUnsubscribe = this._appShell.onEvent(
      this._onAppEvent.bind(this)
    );

    this._notifyRenderCallbacks();
  }

  /**
   * Hide the eliminated player screen and stop listening for events.
   */
  hide(): void {
    if (!this._isVisible) {
      return;
    }

    this._isVisible = false;

    if (this._eventUnsubscribe) {
      this._eventUnsubscribe();
      this._eventUnsubscribe = null;
    }
  }

  /**
   * Reset the eliminated player screen state.
   */
  reset(): void {
    this._wasEliminated = false;
  }

  // ─── Render Callbacks ────────────────────────────────────────────────────

  /**
   * Subscribe to render data changes.
   * Returns an unsubscribe function.
   */
  onRenderChange(callback: EliminatedPlayerRenderCallback): () => void {
    this._renderCallbacks.push(callback);
    return () => {
      const index = this._renderCallbacks.indexOf(callback);
      if (index !== -1) {
        this._renderCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe to elimination events.
   * Returns an unsubscribe function.
   */
  onEliminated(callback: EliminatedCallback): () => void {
    this._eliminatedCallbacks.push(callback);
    return () => {
      const index = this._eliminatedCallbacks.indexOf(callback);
      if (index !== -1) {
        this._eliminatedCallbacks.splice(index, 1);
      }
    };
  }

  // ─── Internal Event Handling ─────────────────────────────────────────────

  private _onAppEvent(event: AppShellEvent): void {
    switch (event.type) {
      case 'stateChange': {
        const renderData = this._buildRenderData();
        const justEliminated = renderData.isEliminated && !this._wasEliminated;

        if (justEliminated) {
          this._notifyEliminatedCallbacks(renderData.finalPlacement);
        }

        this._wasEliminated = renderData.isEliminated;
        this._notifyRenderCallbacks();
        break;
      }
      case 'screenChange': {
        const data = event.data as { previousScreen: string; currentScreen: string };
        // Hide when navigating away from match screen (unless going to gameOver)
        if (data.currentScreen !== 'match' && data.currentScreen !== 'gameOver' && this._isVisible) {
          this.hide();
        }
        break;
      }
    }
  }

  // ─── Internal ────────────────────────────────────────────────────────────

  private _notifyRenderCallbacks(): void {
    const data = this._buildRenderData();
    for (const callback of this._renderCallbacks) {
      callback(data);
    }
  }

  private _notifyEliminatedCallbacks(placement: number | null): void {
    for (const callback of this._eliminatedCallbacks) {
      callback(placement);
    }
  }

  private _buildRenderData(): EliminatedPlayerRenderData {
    const matchState = this._appShell.gameState.match;
    const allPlayers = matchState.players;

    // Find current player's data
    const currentPlayer = allPlayers.find((p) => p.id === this._playerId) ?? null;
    const isEliminated = currentPlayer
      ? !currentPlayer.isAlive || currentPlayer.phase === 'eliminated' || currentPlayer.life <= 0
      : false;

    // Count living players (excluding the current player if eliminated)
    const livingPlayers = allPlayers.filter(
      (p) => p.isAlive && p.phase !== 'eliminated' && p.life > 0
    );
    const livingPlayersCount = livingPlayers.length;
    const totalPlayers = allPlayers.length;

    // Compute the FINAL PLACEMENT from the ORDER OF DEATH (authoritative):
    // each player's eliminationRound + eliminatedDuringPreparation — set by
    // the RoundCoordinator at the moment of death — is the order key, and
    // the placement is FROZEN at the moment of death: a player who survived
    // longer is ALWAYS placed above the one who died earlier, and the
    // number must not improve as the other players die afterwards.
    // Gold and life never factor in. (The old `livingPlayersCount + 1` was
    // re-derived from the CURRENT living count on every state update, so a
    // player who died in round 7 of an 8-player match would "climb" to 2nd
    // place as the last opponent fought on to the end.) Players without a
    // recorded key (legacy state) tie at the earliest position — identical
    // to the old living+1 result for such states.
    let finalPlacement: number | null = null;
    if (isEliminated && currentPlayer) {
      const myKey = eliminationOrderKey(currentPlayer);
      let aheadOfMe = 0;
      for (const p of allPlayers) {
        if (p.id === this._playerId) continue;
        if (eliminationOrderKey(p) > myKey) {
          aheadOfMe++;
        }
      }
      finalPlacement = aheadOfMe + 1;
    }

    // Match over if only one player is alive
    const isMatchOver = livingPlayersCount <= 1 && matchState.gameOver;
    const winnerId = matchState.winnerId;

    // Build elimination message
    const eliminationMessage = this._buildEliminationMessage(
      isEliminated,
      finalPlacement,
      totalPlayers,
      livingPlayersCount,
      isMatchOver,
      winnerId
    );

    // When eliminated, always hide player controls
    const hidePlayerControls = isEliminated;

    return {
      isVisible: isEliminated,
      isEliminated,
      finalPlacement,
      totalPlayers,
      livingPlayersCount,
      isMatchOver,
      winnerId,
      eliminationMessage,
      hidePlayerControls,
      currentRound: matchState.currentRound,
      matchId: matchState.matchId,
      finalGold: currentPlayer?.gold ?? 0,
      finalLife: currentPlayer?.life ?? 0,
    };
  }

  private _buildEliminationMessage(
    isEliminated: boolean,
    finalPlacement: number | null,
    totalPlayers: number,
    livingPlayersCount: number,
    isMatchOver: boolean,
    winnerId: string | null,
  ): string {
    if (!isEliminated) {
      return '';
    }

    const placementText = finalPlacement !== null
      ? `You placed ${this._getOrdinal(finalPlacement)} out of ${totalPlayers} players.`
      : 'You have been eliminated from the match.';

    if (isMatchOver) {
      if (winnerId === this._playerId) {
        return 'You won the match!'; // Shouldn't happen for eliminated player, but safe default
      }
      return `${placementText} ${winnerId} won the match.`;
    }

    if (livingPlayersCount > 0) {
      return `${placementText} ${livingPlayersCount} player${livingPlayersCount > 1 ? 's' : ''} remain${livingPlayersCount === 1 ? 's' : ''}.`;
    }

    return placementText;
  }

  private _getOrdinal(n: number): string {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
  }
}
